import { Alert, Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { attemptedSections, liveSectionErrors } from '../../domain/setup/liveSetupRules';
import { SETUP_SECTIONS, sectionById } from '../../domain/setup/sections';
import { hasRegistryPermission } from '../../session/RegistrySession';
import { PageHeader, StatusChip } from '../../ui/primitives';
import type { RegistryDealSetup } from '../../api/registryDealTypes';
import type { RegistrySession } from '../../session/RegistrySession';
import type { SetupSection } from '../../domain/setup/fieldTypes';
import LiveSetupStep from './LiveSetupStep';
import SetupSideNav, { isSectionLocked } from './SetupSideNav';
import { useLiveSetup } from './useLiveSetup';

/** Steps holding a field the backend rejected on the last activate or send. */
function sectionsWithServerErrors(fieldErrors: Record<string, string>): string[] {
  const keys = Object.keys(fieldErrors);
  return SETUP_SECTIONS.filter((section) =>
    section.blocks.some((block) => block.fields.some((field) => keys.includes(field.key))),
  ).map((section) => section.id);
}

function isReadOnly(setup: RegistryDealSetup, section: SetupSection, session: RegistrySession) {
  return (
    !hasRegistryPermission(session, 'RegistrySetup') ||
    setup.status === 'PUBLISHED' ||
    setup.offer?.status === 'PENDING_APPROVAL' ||
    (section.group !== 'OFFER' && setup.status !== 'DRAFT')
  );
}

/** The requested step, unless it is missing or locked; then where this setup starts. */
function chosenSection(setup: RegistryDealSetup, requested: string): SetupSection {
  const found = sectionById(requested);
  if (found && !isSectionLocked(setup, found.group)) return found;
  return sectionById(setup.status === 'DRAFT' ? 'trust' : 'offer')!;
}

function SetupTitle({ name, status }: { name: string; status: RegistryDealSetup['status'] }) {
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      justifyContent="space-between"
      component="span"
    >
      <span>{name}</span>
      <Box component="span" sx={{ flexShrink: 0 }}>
        <StatusChip status={status} large />
      </Box>
    </Stack>
  );
}

export default function LiveSetupWizardPage() {
  const { setupId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const state = useLiveSetup(setupId);
  const { session, setup, values, error, fieldErrors, attempts } = state;
  if (!setup)
    return error ? (
      <Alert severity="error">{error}</Alert>
    ) : (
      <Typography>Loading deal setup…</Typography>
    );
  const section = chosenSection(setup, params.get('step') ?? '');
  const readOnly = isReadOnly(setup, section, session);
  const navigateStep = (id: string) => {
    setParams({ step: id });
    window.scrollTo({ top: 0 });
  };
  const serverFlagged = sectionsWithServerErrors(fieldErrors);

  return (
    <>
      <PageHeader
        eyebrow="Live from Fides"
        title={
          <SetupTitle
            name={String(values['fund.name'] ?? setup.fundOpportunity.name)}
            status={setup.status}
          />
        }
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {setup.offer?.rejectionComment && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Last rejection: {setup.offer.rejectionComment}
        </Alert>
      )}
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', lg: '240px minmax(0, 1fr)' },
        }}
      >
        <Box>
          <SetupSideNav
            setup={{ ...setup, values }}
            current={section.id}
            onSelect={navigateStep}
            isValid={(id) =>
              !serverFlagged.includes(id) && Object.keys(liveSectionErrors(id, values)).length === 0
            }
            flagged={[...attemptedSections(attempts), ...serverFlagged]}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <LiveSetupStep
            {...state}
            setup={setup}
            setupId={setupId}
            section={section}
            readOnly={readOnly}
            navigateStep={navigateStep}
          />
        </Box>
      </Box>
    </>
  );
}
